import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faBuilding, faXmark } from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../../utils/constants'
import useAutoDismissSnack from '../../../Hooks/AutoDismissSnack'
import Navigation from '../../../Layouts/Navigation/Navigation'

function ControlApartments() {
    const { id_torre } = useParams();
    const [apartments, setApartments] = useState([]);
    const [filteredApartments, setFilteredApartments] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [num_apartamento, setNumApartamento] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [open, setOpen] = React.useState(false);
    const [pisos, setPisos] = useState([]);
    const [piso, setPiso] = useState('');

    const [selectedApartment, setSelectedApartment] = useState(null);
    const [editOpen, setEditOpen] = React.useState(false);

    const [openSnack, setOpenSnack] = React.useState(false);
    const [snackStatus, setSnackStatus] = useState("");
    const [snackMessage, setSnackMessage] = useState("");

    const handleClickSnack = () => {
        setOpenSnack(true);
    };
    
    const handleCloseSnack = () => {
        setOpenSnack(false);
    };
    useAutoDismissSnack(openSnack, setOpenSnack);

    const handleChangePage = (event, newPage) => { 
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const getApartments = () => {
        axios.get(`${BASE_URL}/getApartmentsData/${id_torre}`)
        .then(response => {
            setApartments(response.data);
            setFilteredApartments(response.data);
        })
        .catch(error => console.error('Error fetching apartments:', error));
    }

    useEffect(() => {
        getApartments();
    }, [id_torre]);

    const handleClickGetPisos = () => {
        setOpen(true);
        axios.get(`${BASE_URL}/getPisosByTower/${id_torre}`)
        .then(response => {
            setPisos(response.data);
        })
        .catch(error => console.error('Error fetching floors:', error));
    }

    const handleAddSubmit = (event) => {
        event.preventDefault();

        const apartmentData = {
            piso: piso,
            num_apartamento: num_apartamento,
            id_torre: id_torre
        }

        axios.post(`${BASE_URL}/addApartment`, apartmentData)
        .then(response => {
            if (response.status === 200) {
                setSnackStatus("success");
                setSnackMessage("Apartamento agregado correctamente");
                getApartments();
            } else if (response.status === 500) {
                setSnackStatus("error");
                setSnackMessage("Error al agregar el apartamento");
            }
            handleClickSnack();
        })
        .catch(error => {
            console.error('Error adding apartment:', error);
            setSnackStatus("error");
            setSnackMessage("Error al agregar el apartamento");
            handleClickSnack();
        });
    }

    const handleEditApartment = (apartment) => {
        axios.get(`${BASE_URL}/getPisosByTower/${id_torre}`)
        .then(response => {
            setPisos(response.data);
            setSelectedApartment(apartment);
    
            // Abre el diálogo solo después de que las opciones estén disponibles
            setEditOpen(true);
        })
        .catch(error => console.error('Error fetching floors:', error));
    }
        

    const HandleEditSubmit = (event) => {
        event.preventDefault();

        const apartmentData = {
            piso: selectedApartment.piso,
            num_apartamento: selectedApartment.num_apartamento,
            id_torre: id_torre
        }

        axios.put(`${BASE_URL}/updateApartment/${selectedApartment.id_apartamento}`,{
            ...apartmentData,
            id_apartamento: selectedApartment.id_apartamento
        })
        .then(response => {
            if (response.status === 200) {
                setSnackStatus("success");
                setSnackMessage("Apartamento editado correctamente");
                getApartments();
            } else if (response.status === 500) {
                setSnackStatus("error");
                setSnackMessage("Error al editar el apartamento");
            }
            handleClickSnack();
        })
        .catch(error => {
            console.error('Error editing apartment:', error);
            setSnackStatus("error");
            setSnackMessage("Error al editar el apartamento");
            handleClickSnack();
        });

        setEditOpen(false);
        getApartments();
    }

    const handleSearch = (event) => {
        const value = event.target.value.toLowerCase();
        setSearchTerm(value);
        const filtered = apartments.filter((apartment) =>
            apartment.num_apartamento.toLowerCase().includes(value) ||
            apartment.piso.toString().includes(value)
        );
        setFilteredApartments(filtered);
        setPage(0);
    };

  return (
    <>
        <Navigation/>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Snackbar Alert */}
                {openSnack && (
                    <div className={`fixed top-4 right-4 z-[9999] p-4 rounded-lg shadow-lg ${
                        snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'
                    } text-white animate-fade-in-down`}>
                        <div className="flex items-center gap-2">
                            <span>{snackMessage}</span>
                            <button onClick={handleCloseSnack} className="ml-2">
                                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-center gap-3 mb-6">
                        <FontAwesomeIcon icon={faBuilding} className="h-10 w-10 text-primary-600" />
                        <h1 className="text-4xl font-bold text-gray-800">Apartamentos de la Torre {id_torre}</h1>
                    </div>
                    
                    {/* Search and Add Button */}
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
                        <div className="flex-1 w-full sm:max-w-md">
                            <input
                                type="text"
                                placeholder="Buscar por número de apartamento o piso..."
                                value={searchTerm}
                                onChange={handleSearch}
                                className="input-field w-full"
                            />
                        </div>
                        <button
                            onClick={handleClickGetPisos}
                            className="btn-primary flex items-center gap-2 whitespace-nowrap"
                        >
                            <FontAwesomeIcon icon={faBuilding} className="h-5 w-5" />
                            Agregar Apartamento
                        </button>
                    </div>
                </div>

                {/* Modal para agregar apartamento */}
                {open && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                                <h2 className="text-2xl font-bold text-gray-800">Agregar Apartamento</h2>
                                <p className="text-gray-600 mt-1">Ingrese el número del apartamento</p>
                            </div>
                            <form onSubmit={handleAddSubmit}>
                                <div className="p-6 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Número de Apartamento *
                                            </label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                value={num_apartamento}
                                                onChange={(e) => setNumApartamento(e.target.value)}
                                                required
                                                autoFocus
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Piso *
                                            </label>
                                            <select
                                                className="input-field"
                                                value={piso}
                                                onChange={(e) => setPiso(e.target.value)}
                                                required
                                            >
                                                <option value="">Seleccione un piso</option>
                                                {pisos[0] 
                                                    ? [...Array(pisos[0].cant_pisos)].map((_, i) => (
                                                        <option value={i + 1} key={i + 1}>
                                                            Piso {i + 1}
                                                        </option>
                                                    ))
                                                    : null
                                                }
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-primary"
                                    >
                                        Agregar Apartamento
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Modal para editar apartamento */}
                {editOpen && selectedApartment && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                                <h2 className="text-2xl font-bold text-gray-800">Editar Apartamento</h2>
                                <p className="text-gray-600 mt-1">Modifique la información del apartamento</p>
                            </div>
                            <form onSubmit={HandleEditSubmit}>
                                <div className="p-6 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Número de Apartamento *
                                            </label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                value={selectedApartment.num_apartamento}
                                                onChange={(event) => setSelectedApartment({...selectedApartment, num_apartamento: event.target.value})}
                                                required
                                                autoFocus
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Piso *
                                            </label>
                                            <select
                                                className="input-field"
                                                value={selectedApartment.piso}
                                                onChange={(event) => setSelectedApartment({...selectedApartment, piso: event.target.value})}
                                                required
                                            >
                                                <option value="">Seleccione un piso</option>
                                                {pisos[0]
                                                    ? [...Array(pisos[0].cant_pisos)].map((_, i) => (
                                                        <option value={i + 1} key={i + 1}>
                                                            Piso {i + 1}
                                                        </option>
                                                    ))
                                                    : null
                                                }
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => setEditOpen(false)}
                                        className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-primary"
                                    >
                                        Guardar Cambios
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gradient-to-r from-primary-500 to-primary-700">
                                <tr>
                                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">
                                        Piso
                                    </th>
                                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">
                                        Apartamento
                                    </th>
                                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredApartments
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((apartment) => (
                                        <tr key={apartment.id_apartamento} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 text-center text-sm font-medium text-gray-900">
                                                {apartment.piso}
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm text-gray-700">
                                                {apartment.num_apartamento}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <button
                                                    onClick={() => handleEditApartment(apartment)}
                                                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm hover:shadow-md"
                                                    title="Editar apartamento"
                                                >
                                                    <FontAwesomeIcon icon={faPenToSquare} className="h-5 w-5" />
                                                    Editar
                                                </button>
                                            </td>
                                        </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Pagination */}
                    <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-700">Filas por página:</span>
                            <select
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                className="input-field py-1 px-2 text-sm"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-700">
                                {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredApartments.length)} de {filteredApartments.length}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={(e) => handleChangePage(e, page - 1)}
                                    disabled={page === 0}
                                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                >
                                    Anterior
                                </button>
                                <button
                                    onClick={(e) => handleChangePage(e, page + 1)}
                                    disabled={page >= Math.ceil(filteredApartments.length / rowsPerPage) - 1}
                                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>
  )
}

export default ControlApartments
